import { z } from 'zod';
import { diaMadrid } from './fechas';
import { fmtDuracion, horaMadrid } from './presentacion';

// El parte creado en Odoo, tal como lo anota en `datos` quien escribe el hito
// `parte_creado`: lo que Odoo GUARDÓ al releerlo tras crearlo, no lo que se le
// mandó. Es una foto del momento de la creación; un cambio posterior en Odoo no
// llega aquí.
//
// Se valida en la frontera porque `datos` es jsonb libre: sin un nº de parte no
// hay tarjeta, y un campo raro se descarta sin tumbar el resto (`catch`), que la
// tarjeta enseñe menos es preferible a que no enseñe nada.
const texto = z.string().trim().min(1).optional().catch(undefined);

// Instante ISO 8601 CON zona (Z o ±hh:mm) y de un día que exista. Odoo devuelve sus
// fechas en UTC sin zona («2026-09-28 09:05:06») y un navegador las lee como hora
// LOCAL; y `new Date` convierte un 30 de febrero en 2 de marzo sin avisar. Las dos
// se descartan: el escritor manda ISO con zona, y lo que no lo sea no se pinta.
const ISO_CON_ZONA =
  /^(\d{4})-(\d{2})-(\d{2})T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})$/;
function esInstanteIso(v: string): boolean {
  const m = ISO_CON_ZONA.exec(v);
  if (!m || Number.isNaN(Date.parse(v))) return false;
  const [a, mes, dia] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const f = new Date(Date.UTC(a, mes - 1, dia));
  return f.getUTCFullYear() === a && f.getUTCMonth() === mes - 1 && f.getUTCDate() === dia;
}
const instante = z.string().trim().refine(esInstanteIso).optional().catch(undefined);

const EsquemaParte = z.object({
  id_odoo: z.number().int().positive(),
  titulo: texto,
  cliente: texto,
  direccion_entrega: texto,
  equipos: z.array(z.string().trim().min(1)).optional().catch(undefined),
  proyecto: texto,
  rama: texto,
  linea_negocio: texto,
  etapa: texto,
  tecnicos: z.array(z.string().trim().min(1)).optional().catch(undefined),
  llamada: z
    .object({
      fecha: instante,
      duracion_s: z.number().int().nonnegative().optional().catch(undefined),
      motivo: texto,
    })
    .optional()
    .catch(undefined),
  adjuntos: z
    .array(z.enum(['resumen', 'transcripcion']))
    .optional()
    .catch(undefined),
  descripcion: texto,
  creado_en: instante,
  entorno: z.enum(['test', 'produccion']).optional().catch(undefined),
});

export type ParteOdoo = z.infer<typeof EsquemaParte>;

export function parseParteOdoo(datos: unknown): ParteOdoo | null {
  const r = EsquemaParte.safeParse(datos);
  return r.success ? r.data : null;
}

export interface FilaParte {
  etiqueta: string;
  valor: string;
}

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

// Día corto y hora en Europe/Madrid, o null si la fecha no se lee: una fecha rara
// se calla en vez de pintar un «—» dentro de una frase.
function momento(iso: string | undefined): { dia: string; hora: string } | null {
  if (!iso || Number.isNaN(new Date(iso).getTime())) return null;
  const [, mes, dia] = diaMadrid(iso).split('-');
  return { dia: `${Number(dia)} ${MESES[Number(mes) - 1]}`, hora: horaMadrid(iso) };
}

const ORDEN_ADJUNTOS = ['resumen', 'transcripcion'] as const;
const NOMBRE_ADJUNTO: Record<(typeof ORDEN_ADJUNTOS)[number], string> = {
  resumen: 'resumen',
  transcripcion: 'transcripción',
};

// Filas de la tarjeta, en orden fijo. Solo sale lo que trae el parte: una fila
// sin dato no se pinta, y «sin asignar» exige saber que la lista de técnicos
// está vacía, no que falta. Lo mismo «sin identificar» con los equipos: los
// partes anotados antes del 29-sep no traen la lista y no pintan la fila.
export function filasParte(p: ParteOdoo): FilaParte[] {
  const filas: FilaParte[] = [];
  const poner = (etiqueta: string, valor: string | undefined) => {
    if (valor) filas.push({ etiqueta, valor });
  };

  poner('Cliente', p.cliente);
  poner('Dirección de entrega', p.direccion_entrega);
  if (p.equipos) {
    poner(
      p.equipos.length > 1 ? 'Equipos' : 'Equipo',
      p.equipos.length ? p.equipos.join(' · ') : 'sin identificar'
    );
  }
  poner('Proyecto', p.proyecto);
  poner('Rama · Línea', [p.rama, p.linea_negocio].filter(Boolean).join(' · '));
  if (p.tecnicos) poner('Técnico', p.tecnicos.length ? p.tecnicos.join(', ') : 'sin asignar');
  if (p.llamada) {
    const m = momento(p.llamada.fecha);
    const duracion = p.llamada.duracion_s !== undefined ? fmtDuracion(p.llamada.duracion_s) : null;
    poner(
      'Llamada',
      [m ? `${m.dia}, ${m.hora}` : null, duracion, p.llamada.motivo].filter(Boolean).join(' · ')
    );
  }
  if (p.adjuntos?.length) {
    const adjuntos = ORDEN_ADJUNTOS.filter((a) => p.adjuntos?.includes(a));
    poner('Adjuntos', `${adjuntos.map((a) => NOMBRE_ADJUNTO[a]).join(' y ')} de la llamada`);
  }
  poner('Descripción', p.descripcion);
  return filas;
}

// Pie de la tarjeta: cuándo se creó y, si es el caso, que es el Odoo de pruebas.
export function pieParte(p: ParteOdoo): string | null {
  const m = momento(p.creado_en);
  const partes = [
    m ? `creado en Odoo el ${m.dia} a las ${m.hora}` : null,
    p.entorno === 'test' ? 'entorno de pruebas' : null,
  ].filter((x): x is string => x !== null);
  if (partes.length === 0) return null;
  const pie = partes.join(' · ');
  return pie.charAt(0).toUpperCase() + pie.slice(1);
}
